import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { HeatmapDataType } from '@demind-inc/core';
import { ZONE_COLOR } from './contentCardUtils';
import { GradientEnergyWaveform } from '../../Energy';
import type {
  EnergySummaryPayload,
  RichContextEnergyZone,
} from '../../../data-access/api/science/openapi/api';
import { useAuthContext, useCircadianHeatmap, skimHeatmapCurve } from '../../../data-access';
import './richContext.scss';
import { useTranslation } from 'react-i18next';
import type { DataTFunction } from '../../DataView/i18n';

const normalizeZoneName = (value?: string): string => (value ? value.trim().toLowerCase() : '');

const formatZoneName = (t: DataTFunction, value?: string): string => {
  if (!value) return t('task.richContext.energy.unknown');
  const key = value.trim().toLowerCase();
  const fallback = key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return t(`task.richContext.energy.zones.${key}`, { defaultValue: fallback });
};

const toWindowText = (t: DataTFunction, start?: string, end?: string): string | null => {
  if (start && end) return `${start} - ${end}`;
  if (start) return t('task.richContext.energy.startsAt', { time: start });
  if (end) return t('task.richContext.energy.endsAt', { time: end });
  return null;
};

const resolveZoneColor = (zoneName?: string): string => {
  const normalized = normalizeZoneName(zoneName);
  if (!normalized) return 'rgba(19,19,20,0.15)';
  if (ZONE_COLOR[normalized]) return ZONE_COLOR[normalized];
  if (normalized.includes('high') || normalized.includes('peak')) return ZONE_COLOR.peak;
  if (normalized.includes('low') || normalized.includes('dip')) return ZONE_COLOR.low;
  if (normalized.includes('calm')) return ZONE_COLOR.calm;
  if (normalized.includes('recover')) return ZONE_COLOR.recovering;
  return 'rgba(19,19,20,0.15)';
};

const parseTimeToMinutes = (value?: string): number | null => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (ampmMatch) {
    const hourPart = Number(ampmMatch[1]);
    const minutePart = Number(ampmMatch[2]);
    if (Number.isNaN(hourPart) || Number.isNaN(minutePart) || minutePart < 0 || minutePart > 59)
      return null;
    const period = ampmMatch[3].toUpperCase();
    const hour12 = hourPart % 12;
    const hour24 = period === 'PM' ? hour12 + 12 : hour12;
    return hour24 * 60 + minutePart;
  }
  const hhmmMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmmMatch) {
    const hourPart = Number(hhmmMatch[1]);
    const minutePart = Number(hhmmMatch[2]);
    if (
      Number.isNaN(hourPart) ||
      Number.isNaN(minutePart) ||
      hourPart < 0 ||
      hourPart > 23 ||
      minutePart < 0 ||
      minutePart > 59
    )
      return null;
    return hourPart * 60 + minutePart;
  }
  const parsedDate = dayjs(trimmed);
  if (parsedDate.isValid()) return parsedDate.hour() * 60 + parsedDate.minute();
  return null;
};

const isTimeInZoneRange = (
  currentMinute: number,
  startMinute: number,
  endMinute: number
): boolean => {
  if (startMinute === endMinute) return true;
  if (startMinute < endMinute) return currentMinute >= startMinute && currentMinute < endMinute;
  return currentMinute >= startMinute || currentMinute < endMinute;
};

const findCurrentZoneIndex = (
  zones: RichContextEnergyZone[],
  now: dayjs.Dayjs,
  currentZone?: string
): number => {
  if (zones.length === 0) return -1;
  const nowMinutes = now.hour() * 60 + now.minute();
  const byTimeIndex = zones.findIndex((zone) => {
    const startMinute = parseTimeToMinutes(zone.start);
    const endMinute = parseTimeToMinutes(zone.end);
    if (startMinute === null || endMinute === null) return false;
    return isTimeInZoneRange(nowMinutes, startMinute, endMinute);
  });
  if (byTimeIndex >= 0) return byTimeIndex;
  if (!currentZone) return -1;
  const current = normalizeZoneName(currentZone);
  return zones.findIndex((zone) => {
    const label = normalizeZoneName(zone.label);
    const level = normalizeZoneName(zone.level);
    return label === current || level === current;
  });
};

const ENERGY_CURVE_HEIGHT = 100;

const calendarDate = (value?: string): string => {
  if (value && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  return dayjs().format('YYYY-MM-DD');
};

/** Payload heatmap is already the skimmed curve. Drop zero-or-below values,
 * sample dense series the same way the energy-result chart does
 * (see skimHeatmapCurve). */
const heatmapFromPayload = (raw: unknown): HeatmapDataType[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  const points = raw.flatMap((point) => {
    if (!point || typeof point !== 'object') {
      return [];
    }
    const record = point as Record<string, unknown>;
    const value = record.value;
    const y = record.y ?? record.timestamp;
    if (
      typeof value !== 'number' ||
      !Number.isFinite(value) ||
      value <= 0 ||
      typeof y !== 'string'
    ) {
      return [];
    }
    return [
      {
        x: typeof record.x === 'string' ? record.x : 'dummy-same',
        y,
        value,
      } as HeatmapDataType,
    ];
  });

  return points.length > 400 ? skimHeatmapCurve(points) : points;
};

type Props = {
  p: EnergySummaryPayload;
  /** Calendar day (`YYYY-MM-DD`) the summary describes. Defaults to today. */
  date?: string;
};

const EnergySummaryContent: React.FC<Props> = ({ p, date }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const payload = p as Record<string, any>;
  const zones: RichContextEnergyZone[] = Array.isArray(payload.zones) ? payload.zones : [];
  const targetDate = calendarDate(date);

  // Live circadian data is the source of truth for the curve — the payload's
  // own `heatmap` (when present) is preferred since it's already scoped to
  // this suggestion, but most responses only carry `zones`, so this is the
  // common path. Same query as EnergyPredictionResultPage/
  // EnergyResultStepTemplate, filtered/skimmed the same way (skimHeatmapCurve).
  const { circadianRhythmsByDate } = useCircadianHeatmap({
    userId: user.userId,
    calendarIds: [],
    metricId: user.metricId ?? '',
    dates: [targetDate],
  });

  const heatmapCurve = useMemo(() => {
    const fromPayload = heatmapFromPayload(payload.heatmap);
    if (fromPayload.length >= 2) {
      return fromPayload;
    }
    const values = circadianRhythmsByDate.find((item) => item.date === targetDate)?.values ?? [];
    const skimmed = skimHeatmapCurve(values);
    return skimmed.length >= 2 ? skimmed : null;
  }, [circadianRhythmsByDate, payload.heatmap, targetDate]);

  const currentWindow =
    payload.current_window && typeof payload.current_window === 'object'
      ? (payload.current_window as RichContextEnergyZone)
      : undefined;
  const fallbackCurrentZone = typeof payload.current_zone === 'string' ? payload.current_zone : '';
  const currentZoneIndex = findCurrentZoneIndex(zones, dayjs(), fallbackCurrentZone);
  const currentZoneItem = currentZoneIndex >= 0 ? zones[currentZoneIndex] : undefined;
  const nextZoneFromList =
    currentZoneIndex >= 0 && currentZoneIndex < zones.length - 1
      ? zones[currentZoneIndex + 1]
      : undefined;
  const nextZoneRaw =
    payload.next_zone && typeof payload.next_zone === 'object'
      ? (payload.next_zone as RichContextEnergyZone)
      : undefined;

  const currentZoneEnd =
    (typeof payload.current_zone_end === 'string' && payload.current_zone_end) ||
    (typeof currentWindow?.end === 'string' ? currentWindow.end : undefined) ||
    (typeof currentZoneItem?.end === 'string' ? currentZoneItem.end : undefined);
  const currentZoneStart =
    (typeof payload.current_zone_start === 'string' && payload.current_zone_start) ||
    (typeof currentWindow?.start === 'string' ? currentWindow.start : undefined) ||
    (typeof currentZoneItem?.start === 'string' ? currentZoneItem.start : undefined);
  const currentZoneLabel =
    currentZoneItem?.label || currentZoneItem?.level || fallbackCurrentZone || currentWindow?.level;
  const nextZoneLabel =
    (typeof payload.next_zone_label === 'string' && payload.next_zone_label) ||
    (typeof nextZoneRaw?.label === 'string' && nextZoneRaw.label) ||
    (typeof nextZoneRaw?.level === 'string' && nextZoneRaw.level) ||
    (typeof nextZoneFromList?.label === 'string' && nextZoneFromList.label) ||
    (typeof nextZoneFromList?.level === 'string' && nextZoneFromList.level) ||
    undefined;
  const nextZoneStart =
    (typeof payload.next_zone_start === 'string' && payload.next_zone_start) ||
    (typeof nextZoneRaw?.start === 'string' && nextZoneRaw.start) ||
    (typeof nextZoneFromList?.start === 'string' ? nextZoneFromList.start : undefined) ||
    undefined;
  const nextZoneEnd =
    (typeof payload.next_zone_end === 'string' && payload.next_zone_end) ||
    (typeof nextZoneRaw?.end === 'string' && nextZoneRaw.end) ||
    (typeof nextZoneFromList?.end === 'string' ? nextZoneFromList.end : undefined) ||
    undefined;
  const minutesToNextZone =
    typeof payload.minutes_to_next_zone === 'number' &&
    Number.isFinite(payload.minutes_to_next_zone)
      ? Math.max(0, Math.round(payload.minutes_to_next_zone as number))
      : undefined;

  const currentWindowText = toWindowText(
    t,
    currentZoneStart || undefined,
    currentZoneEnd || undefined
  );
  const nextWindowText = toWindowText(t, nextZoneStart || undefined, nextZoneEnd || undefined);
  const currentZoneColor = resolveZoneColor(currentZoneLabel);
  const nextZoneColor = resolveZoneColor(nextZoneLabel);
  const currentSecondaryText = currentWindowText || t('task.richContext.energy.inProgress');
  const etaText =
    typeof minutesToNextZone === 'number'
      ? minutesToNextZone === 0
        ? t('task.richContext.energy.startsNow')
        : t('task.richContext.energy.inMinutes', { minutes: minutesToNextZone })
      : null;
  const nextSecondaryText = [nextWindowText || t('task.richContext.energy.noStartTimeYet'), etaText]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rc-content-body">
      {!!heatmapCurve && (
        <div className="rc-energy-curve-wrap">
          <GradientEnergyWaveform
            data={heatmapCurve}
            height={ENERGY_CURVE_HEIGHT}
            useGradient
            showGrid={false}
            smooth
            showCurrentIndicator={targetDate === dayjs().format('YYYY-MM-DD')}
          />
          <div className="rc-energy-curve-axis-row">
            <span className="rc-energy-curve-axis-label">
              {dayjs(heatmapCurve[0].y).format('h A')}
            </span>
            <span className="rc-energy-curve-axis-label">
              {dayjs(heatmapCurve[heatmapCurve.length - 1].y).format('h A')}
            </span>
          </div>
        </div>
      )}
      <div className="rc-energy-status-row">
        <div className="rc-energy-status-item">
          <span className="rc-energy-zone-title">{t('task.richContext.energy.now')}</span>
          <div className="rc-energy-status-value-row">
            <span className="rc-energy-status-dot" style={{ backgroundColor: currentZoneColor }} />
            <span className="rc-energy-status-value">{formatZoneName(t, currentZoneLabel)}</span>
          </div>
          <span className="rc-energy-zone-secondary-text">{currentSecondaryText}</span>
        </div>

        <div className="rc-energy-status-item">
          <span className="rc-energy-zone-title">{t('task.richContext.energy.next')}</span>
          <div className="rc-energy-status-value-row">
            <span
              className="rc-energy-status-dot"
              style={{ backgroundColor: nextZoneLabel ? nextZoneColor : 'rgba(19,19,20,0.15)' }}
            />
            <span className="rc-energy-status-value">
              {nextZoneLabel
                ? formatZoneName(t, nextZoneLabel)
                : t('task.richContext.energy.unavailable')}
            </span>
          </div>
          <span className="rc-energy-zone-secondary-text">{nextSecondaryText}</span>
        </div>
      </div>
    </div>
  );
};

export default EnergySummaryContent;
